/**
 * Autocomplete Input Component
 * Google Places Autocomplete with standard label design
 */

import { forwardRef, useCallback, useEffect, useRef, useState } from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib"
import { loadGoogleMapsAPI } from "@/lib/utils/loadGoogleMaps"

// Google Maps Types
interface GoogleMaps {
  google?: {
    maps?: {
      places?: {
        Autocomplete: new (
          input: HTMLInputElement,
          options: { types: string[]; fields: string[] },
        ) => {
          addListener: (event: string, handler: () => void) => void
          getPlace: () => GooglePlace
        }
      }
    }
  }
}

interface GooglePlaceGeometry {
  location: {
    lat: () => number
    lng: () => number
  }
}

interface GooglePlaceAddressComponent {
  long_name: string
  short_name: string
  types: string[]
}

interface GooglePlace {
  geometry?: GooglePlaceGeometry
  address_components?: GooglePlaceAddressComponent[]
  formatted_address?: string
}

export interface LocationData {
  address: string
  lat: number
  lng: number
  city?: string
  state?: string
  country?: string
  zipCode?: string
}

interface AutocompleteInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string
  onLocationSelect: (location: LocationData) => void
  onInputChange?: (value: string) => void
  error?: string
  onClearError?: () => void
  placeholder?: string
  mode?: "full" | "city"
}

// Helper function to check if Google Places API is available
const isGooglePlacesAPIAvailable = (): boolean => {
  if (typeof window === "undefined" || !window.google) {
    return false
  }

  const maps = (window as unknown as GoogleMaps).google?.maps
  if (!maps || typeof maps !== "object") {
    return false
  }

  return (
    maps.places !== undefined &&
    typeof maps.places === "object" &&
    typeof maps.places.Autocomplete !== "undefined"
  )
}

export const AutocompleteInput = forwardRef<HTMLInputElement, AutocompleteInputProps>(
  (
    {
      label,
      error,
      className = "",
      onClearError,
      onLocationSelect,
      onInputChange,
      required,
      disabled = false,
      placeholder,
      mode = "full",
      ...props
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState("")
    const [mapsLoaded, setMapsLoaded] = useState(false)
    const [mapsError, setMapsError] = useState<string | null>(null)
    const [isGoogleReady, setIsGoogleReady] = useState(false)

    // Ref for the div containing Google Places autocomplete
    const autocompleteContainerRef = useRef<HTMLDivElement>(null)

    // Ref for Google Places autocomplete instance
    const [_autocompleteInstance] = useState<{
      addListener: (event: string, handler: () => void) => void
      getPlace: () => GooglePlace
    } | null>(null)

    // Load Google Maps API on mount
    useEffect(() => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined
      let isMounted = true

      if (!mapsLoaded) {
        loadGoogleMapsAPI()
          .then(() => {
            if (!isMounted) return
            setMapsLoaded(true)
            setMapsError(null)

            // Verify Google Maps Places API is actually available
            if (isGooglePlacesAPIAvailable()) {
              setIsGoogleReady(true)
            } else {
              timeoutId = setTimeout(() => {
                if (!isMounted) return
                if (isGooglePlacesAPIAvailable()) {
                  setIsGoogleReady(true)
                } else {
                  setMapsError("Address autocomplete unavailable. Please enter location manually.")
                }
              }, 500)
            }
          })
          .catch(() => {
            if (!isMounted) return
            setMapsError(
              "Failed to load address autocomplete. You can still enter the location manually.",
            )
          })
      } else if (mapsLoaded && !isGoogleReady) {
        // Already loaded, just verify it's ready
        if (isGooglePlacesAPIAvailable()) {
          setIsGoogleReady(true)
        }
      }

      return () => {
        isMounted = false
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId)
        }
      }
    }, [mapsLoaded, isGoogleReady])

    // Update input value when prop changes
    useEffect(() => {
      let retryTimeoutId: ReturnType<typeof setTimeout> | undefined
      let isMounted = true

      if (props.value !== undefined) {
        setInputValue(props.value as string)

        // Set Google Places input value after it's ready
        if (props.value && isGoogleReady) {
          const setGooglePlacesValue = () => {
            if (!isMounted) return
            if (autocompleteContainerRef.current && isGoogleReady) {
              try {
                // Find the actual input element within the Google Places component
                const inputElement = autocompleteContainerRef.current.querySelector("input")
                if (inputElement) {
                  inputElement.value = props.value as string
                }
              } catch {
                // Silently handle error when setting Google Places input value
              }
            } else if (isGoogleReady) {
              // Try again after a short delay if ref isn't ready
              retryTimeoutId = setTimeout(setGooglePlacesValue, 100)
            }
          }

          setGooglePlacesValue()
        }
      }

      return () => {
        isMounted = false
        if (retryTimeoutId !== undefined) {
          clearTimeout(retryTimeoutId)
        }
      }
    }, [props.value, isGoogleReady])

    const handlePlaceSelected = useCallback(
      (place: GooglePlace) => {
        if (!place?.geometry) {
          return
        }

        const formattedAddress = place.formatted_address || ""

        // Extract address components
        const addressComponents = place.address_components || []
        let state = "",
          city = "",
          country = "",
          zipCode = ""

        addressComponents.forEach((component: GooglePlaceAddressComponent) => {
          if (component.types.includes("administrative_area_level_1")) {
            state = component.short_name
          } else if (component.types.includes("locality")) {
            city = component.long_name
          } else if (component.types.includes("country")) {
            country = component.long_name
          } else if (component.types.includes("postal_code")) {
            zipCode = component.long_name
          }
        })

        const locationData: LocationData = {
          address: formattedAddress,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          city,
          state,
          country,
          zipCode,
        }

        onLocationSelect(locationData)

        setInputValue(formattedAddress)
        onInputChange?.(formattedAddress)
      },
      [onLocationSelect, onInputChange],
    )

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setInputValue(value)
        onInputChange?.(value)
      },
      [onInputChange],
    )

    // Dynamically import Autocomplete component to avoid SSR issues
    const [AutocompleteComponent, setAutocompleteComponent] = useState<React.ComponentType<
      Record<string, unknown>
    > | null>(null)

    useEffect(() => {
      if (typeof window !== "undefined" && mapsLoaded && isGoogleReady) {
        import("react-google-autocomplete").then(module => {
          setAutocompleteComponent(() => module.default)
        })
      }
    }, [mapsLoaded, isGoogleReady])

    // Render Google Places Autocomplete when ready
    if (mapsLoaded && isGoogleReady && AutocompleteComponent) {
      return (
        <div className="relative">
          <div ref={autocompleteContainerRef} className="flex flex-col gap-1.25">
            {label && (
              <Label
                className={cn(
                  "text-xs leading-5 font-normal uppercase",
                  error ? "text-brand-terracotta-bright" : "text-body",
                )}
              >
                {label}
                {required && (
                  <span
                    className={cn(
                      "ms-[-3px]",
                      error ? "text-brand-terracotta-bright" : "text-body",
                    )}
                  >
                    *
                  </span>
                )}
              </Label>
            )}
            <AutocompleteComponent
              onPlaceSelected={handlePlaceSelected}
              onChange={handleInputChange}
              options={
                {
                  types: mode === "city" ? ["(cities)"] : ["geocode", "establishment"],
                  fields: [
                    "address_components",
                    "geometry.location",
                    "place_id",
                    "formatted_address",
                  ],
                } as React.ComponentProps<typeof AutocompleteComponent>
              }
              className={cn(
                "w-full pb-2.5 pt-0 border-0 border-b rounded-none bg-transparent text-sm focus:outline-none transition-all duration-200 text-[var(--color-chat-text)] placeholder:text-text-body-ultra",
                error ? "border-brand-terracotta-bright" : "border-input",
                disabled && "opacity-50 cursor-not-allowed",
                className,
              )}
              placeholder={placeholder || " "}
              value={inputValue}
              disabled={disabled}
            />
          </div>
          {error && <p className="text-xs text-brand-terracotta-bright">{error}</p>}
        </div>
      )
    }

    // Fallback to regular input while loading or on error
    return (
      <div className="relative">
        {label && (
          <Label
            className={cn(
              "mb-4 text-[12px] loading-[20px] uppercase",
              error ? "text-brand-terracotta-bright" : "text-primary",
            )}
          >
            {label}
            {required && (
              <span className={cn("ms-1", error ? "text-brand-terracotta-bright" : "text-primary")}>
                *
              </span>
            )}
          </Label>
        )}
        <Input
          ref={ref}
          {...props}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={e => {
            if (error && onClearError) {
              onClearError()
            }
            props.onFocus?.(e)
          }}
          disabled={disabled || (!mapsError && !mapsLoaded)}
          placeholder={
            mapsError ? "" : !mapsLoaded ? "Loading location autocomplete..." : placeholder || " "
          }
          className={cn(
            "border-0 border-b rounded-none shadow-none px-0 !text-[14px] focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-black placeholder:text-text-body-ultra",
            error && "border-brand-terracotta-bright text-brand-terracotta-bright",
            (disabled || (!mapsError && !mapsLoaded)) && "opacity-50 cursor-not-allowed",
            className,
          )}
        />
        {error && <p className="text-xs text-brand-terracotta-bright">{error}</p>}
      </div>
    )
  },
)

AutocompleteInput.displayName = "AutocompleteInput"
